import React from 'react'
import { ArrowRight, LinkAngled } from 'blaze-ui/icons'
import { Button } from 'blaze-ui/Button'
import { Text } from 'blaze-ui/Text/Text'
import { IconButton } from 'components/IconButton/IconButton'
import { ExternalIntegration } from 'api/clients/ExternalIntegrationsClient'
import { useConnectAccountPopup } from 'components/Onboarding/useConnectAccountPopup'
import { InitiateOauthTrigger } from 'components/ConnectIntegrationTrigger/InitiateOauthTrigger'
import { HelpCircleContained } from 'blaze-ui/icons/24'
import Styles from './TacticIntegration.module.scss'
import cx from 'classnames'
import { SwitchPlatformMenu } from '../SwitchPlatformMenu'
import { BrandPlanIntegrations } from 'types'
import { tacticHasGenericIntegration } from '../utils'

interface UnconnectedIntegrationProps {
  displayName: string
  IconComponent: React.ComponentType<any> | null
  index: number
  integration?: ExternalIntegration
  integrationType: string | null
  isHighlighted?: boolean
  onConnect?: (integrations: ExternalIntegration[]) => void
  tactic?: BrandPlanIntegrations
}

export const UnconnectedIntegration: React.FC<UnconnectedIntegrationProps> = ({
  displayName,
  IconComponent,
  index,
  integration,
  integrationType,
  isHighlighted,
  onConnect: onConnectProp,
  tactic,
}) => {
  const { connect: originalConnect } = useConnectAccountPopup()

  const connect = (popup?: Window, options?: any) => {
    const wrappedOptions = {
      ...options,
      onConnect: (integrations: ExternalIntegration[]) => {
        options?.onConnect?.(integrations)
        onConnectProp?.(integrations)
      },
    }
    return originalConnect(popup, wrappedOptions)
  }

  return (
    <div className={cx(Styles.tacticIntegration, { [Styles.isHighlighted]: isHighlighted })}>
      <Text variant="smallList">
        <div className={Styles.indexIndicator}>{index} </div>
      </Text>

      {IconComponent && (
        <div className={Styles.tacticIcon}>
          <IconComponent size={35} />
        </div>
      )}

      <div className={Styles.tacticName}>
        <Text variant="largeList">{displayName}</Text>
      </div>

      {isHighlighted && (
        <div className={Styles.nextUp}>
          <Text variant="metadata" color="var(--magenta-50)">
            Next up
          </Text>
          <ArrowRight size={15} color="var(--magenta-50)" />
        </div>
      )}

      {tacticHasGenericIntegration(tactic) && <SwitchPlatformMenu integration={integration} tactic={tactic} />}

      <InitiateOauthTrigger provider={integrationType as any} popUp onInitiatedOauth={connect}>
        <Button variant="secondary" size="xl" className={Styles.connectButton} frontIcon={LinkAngled}>
          Connect
        </Button>
      </InitiateOauthTrigger>

      <IconButton
        onPress={() =>
          window.Intercom('showNewMessage', `Hello! Can you help me with connecting the ${displayName} integration?`)
        }
        aria-label="More information"
      >
        <HelpCircleContained size={20} color="var(--dark-80)" />
      </IconButton>
    </div>
  )
}
